<template>
    <div :class="panelClasses">
        <CompassPanel
            ref="compassPanelRef"
            v-model="showPanel"
            :size="CompassEnums.PanelSize.LARGE"
            :title="translations.title"
            enableShrink
            @toggled="emit('toggle-panel-state')"
            @closed="emit('close-panel')"
        >
            <div v-if="isLoading" v-show="!isPanelMinimized" class="email-mass-send-panel__loader">
                <CompassLoader />
            </div>
            <div v-else v-show="!isPanelMinimized" class="email-mass-send-panel__content">
                <div class="email-mass-send-panel__preview">
                    <div class="email-mass-send-panel__field">
                        <span class="typographies-paragraph-medium-black">
                            {{ translations.from }}
                        </span>
                        <span
                            class="typographies-paragraph-medium-regular email-mass-send-panel__field-value"
                        >
                            {{ senderEmail }}
                        </span>
                    </div>
                    <div class="email-mass-send-panel__field">
                        <span class="typographies-paragraph-medium-black">
                            {{ translations.subject }}
                        </span>
                        <span
                            class="typographies-paragraph-medium-regular email-mass-send-panel__field-value rich-text-style"
                            v-html="formattedSubject"
                        />
                    </div>
                    <div
                        class="email-mass-send-panel__body typographies-paragraph-medium-regular rich-text-style"
                        v-html="formattedContent"
                    />
                </div>
                <div class="email-mass-send-panel__bottom">
                    <div v-if="hasTemplateAttachments" class="email-mass-send-panel__attachments">
                        <EmailAttachments
                            v-for="(attachment, index) in templateAttachments"
                            :key="index"
                            :attachment="attachment"
                            removeBtn
                        />
                    </div>
                    <CompassFormItem
                        :label="translations.templateLabel"
                        :orientation="CompassEnums.FormItemOrientation.HORIZONTAL"
                    >
                        <CompassSelect
                            v-model="selectedTemplateId"
                            class="email-mass-send-panel__template-select"
                            menuClass="email-mass-send-panel__template-menu"
                            :disabled="isLoadingTemplate"
                            @change="onTemplateChange"
                        >
                            <CompassDropdownOption
                                v-for="template in templates"
                                :key="template.id"
                                :label="template.name"
                                :value="template.id"
                            />
                        </CompassSelect>
                    </CompassFormItem>
                    <CompassAlert
                        :type="CompassEnums.AlertType.WARNING"
                        :description="warningMessage"
                        :showClose="false"
                    />
                </div>
            </div>
            <template #footer>
                <div class="email-mass-send-panel__footer">
                    <CompassPlainButton
                        :label="translations.cancel"
                        :variant="CompassEnums.PlainButtonVariantType.NEUTRAL"
                        @click="emit('close-panel')"
                    />
                    <template v-if="isFeatureScheduleEmailEnabled">
                        <CompassSplitButton
                            ref="splitSendButtonRef"
                            :label="translations.send"
                            :loading="isSending"
                            :disabled="isLoading"
                            @clickOnLabel="handleSend"
                            @clickOnArrow="showDropdown = !showDropdown"
                        />
                        <CompassDropdown
                            v-model:isVisible="showDropdown"
                            parentRef="splitSendButtonRef"
                            placement="top-end"
                            dropdownClass="email-mass-send-panel__schedule-dropdown"
                            value="scheduleAction"
                            @update:modelValue="openScheduleEmail"
                        >
                            <CompassDropdownOption
                                :label="translations.scheduleTitle"
                                value="scheduleAction"
                                icon="calendar-reschedule"
                                :isCheckable="false"
                            />
                        </CompassDropdown>
                        <ScheduleEmailDialog
                            :visible="showScheduleEmailDialog"
                            @scheduled="handleSendScheduledEmail"
                            @close="showScheduleEmailDialog = false"
                        />
                    </template>
                    <CompassButton
                        v-else
                        :label="translations.send"
                        :loading="isSending"
                        :disabled="isLoading"
                        @click="handleSend"
                    />
                </div>
            </template>
        </CompassPanel>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, inject } from 'vue';
import {
    getEmailSettings,
    getListEmailTemplates,
    getEmailTemplate,
    getEmailTemplateTags,
    fetchSystemEmailTemplate,
    sendBatchEmail,
} from 'api/emailEngine/emailEngineService';
import EmailAttachments from 'vcomponents/email/emailPanels/EmailAttachments.vue';
import ScheduleEmailDialog from 'vcomponents/email/emailPanels/ScheduleEmailDialog.vue';
import Feature from 'Services/Feature';
import { Translator } from 'types/injects/translator.type';
import { PushToast } from 'types/injects/pushToast.type';
import { LEGACY_TAG_TO_NEW } from 'Helpers/email/legacyTagMapping';

interface EmailTemplateOption {
    id: number | string;
    name: string;
    hasAi?: boolean;
}

interface EmailTag {
    key: string;
    name: string;
}

interface Attachment {
    name: string;
    size: number;
    extension: string;
    public_link: string;
}

interface Props {
    docIds: string[];
    linkedtype: string;
    isPanelMinimized: boolean;
}

const SYSTEM_TEMPLATE_ID = 'system:document';

const props = defineProps<Props>();

const emit = defineEmits(['close-panel', 'toggle-panel-state']);

const $t = inject('$t') as Translator;
const $pushToast = inject('$pushToast') as PushToast;

const compassPanelRef = ref<{ isMinimized: boolean } | null>(null);
const splitSendButtonRef = ref(null);
const showDropdown = ref(false);
const showScheduleEmailDialog = ref(false);
const scheduledDate = ref<string | null>(null);
const showPanel = ref(true);
const isLoading = ref(true);
const isLoadingTemplate = ref(false);
const isSending = ref(false);
const senderEmail = ref('');
const templateSubject = ref('');
const templateContent = ref('');
const selectedTemplateId = ref<number | string>(SYSTEM_TEMPLATE_ID);
const automaticallyAttachDocument = ref(false);
const templateAttachments = ref<Attachment[]>([]);
const templates = ref<EmailTemplateOption[]>([]);
const tags = ref<EmailTag[]>([]);

const translations: Record<string, string> = {
    title: $t.__('email.massSend.title'),
    from: $t.__('email.massSend.from'),
    subject: $t.__('email.massSend.subject'),
    templateLabel: $t.__('email.massSend.templateLabel'),
    autoAttachDocument: $t.__('email.massSend.autoAttachDocument'),
    cancel: $t.__('common.actions.cancel'),
    send: $t.__('common.actions.send'),
    scheduleTitle: $t.__('email.ScheduleEmailDialog.title'),
};

const panelClasses = computed<(string | Record<string, boolean>)[]>(() => {
    return ['email-mass-send-panel', 'email-panel', { minimized: props.isPanelMinimized }];
});

const hasTemplateAttachments = computed<boolean>(() => {
    return templateAttachments.value.length > 0;
});

const isFeatureScheduleEmailEnabled = computed<boolean>(() => {
    return Feature.isEnabled('email-engine-scheduled-emails');
});

const isScheduledSend = computed<boolean>(() => {
    return scheduledDate.value !== null;
});

const warningMessage = computed<string>(() => {
    const key = isScheduledSend.value
        ? 'email.massSend.warningScheduled'
        : 'email.massSend.warning';
    const base = $t.n__(key, null, props.docIds.length, { count: props.docIds.length });
    if (automaticallyAttachDocument.value) {
        return `${base}\n${translations.autoAttachDocument}`;
    }
    return base;
});

const replaceTagsWithSpan = (message: string): string => {
    const tagsByKey = new Map(tags.value.map((tag) => [tag.key, tag.name]));
    return message.replace(/%\{?[\w.]+\}?%/g, (match) => {
        const newKey = LEGACY_TAG_TO_NEW[match] ?? match;
        const label = tagsByKey.get(newKey) ?? tagsByKey.get(match) ?? match;
        return `<span class="custom-tags-and-fields custom-tags-and-fields__default typographies-paragraph-small-bold">${label}</span>`;
    });
};

const formattedSubject = computed<string>(() => {
    return replaceTagsWithSpan(templateSubject.value);
});

const formattedContent = computed<string>(() => {
    return replaceTagsWithSpan(templateContent.value);
});

const loadEmailSettings = async (): Promise<void> => {
    const response = await getEmailSettings();
    const { connection, automatically_attach_document } = response.data;
    if (connection) {
        senderEmail.value = connection.email ?? '';
    }
    automaticallyAttachDocument.value = automatically_attach_document ?? false;
};

const loadTags = async (): Promise<void> => {
    const response = await getEmailTemplateTags();
    if (response.data) {
        tags.value = response.data;
    }
};

const loadSystemTemplate = async (): Promise<void> => {
    const { data } = (await fetchSystemEmailTemplate('document', {
        type: props.linkedtype,
    })) as { data: { subject?: string; content?: string } };
    if (data) {
        templateSubject.value = data.subject ?? '';
        templateContent.value = data.content ?? '';
    }
};

const loadTemplates = async (): Promise<void> => {
    const response = await getListEmailTemplates();
    const systemTemplate: EmailTemplateOption = {
        id: SYSTEM_TEMPLATE_ID,
        name: $t.__('email.massSend.systemTemplate'),
    };
    const customTemplates: EmailTemplateOption[] = (response.data ?? []).filter(
        (template: EmailTemplateOption) => !template.hasAi,
    );
    templates.value = [systemTemplate, ...customTemplates];
};

const loadCustomTemplateContent = async (templateId: number): Promise<void> => {
    const data = (await getEmailTemplate(templateId)) as {
        subject?: string;
        message?: string;
        attachments?: Attachment[];
    };
    if (data) {
        templateSubject.value = data.subject ?? '';
        templateContent.value = data.message ?? '';
        templateAttachments.value = data.attachments ?? [];
    }
};

const onTemplateChange = async (templateId: number | string): Promise<void> => {
    if (!templateId) {
        return;
    }
    isLoadingTemplate.value = true;
    templateAttachments.value = [];
    try {
        if (templateId === SYSTEM_TEMPLATE_ID) {
            await loadSystemTemplate();
        } else {
            await loadCustomTemplateContent(templateId as number);
        }
    } finally {
        isLoadingTemplate.value = false;
    }
};

const openScheduleEmail = (): void => {
    showDropdown.value = false;
    showScheduleEmailDialog.value = true;
};

const handleSendScheduledEmail = (date: string): void => {
    scheduledDate.value = date;
    showScheduleEmailDialog.value = false;
    handleSend();
};

const buildBatchPayload = (): Record<string, unknown> => {
    const isSystemTemplate = selectedTemplateId.value === SYSTEM_TEMPLATE_ID;
    const template = isSystemTemplate
        ? { type: 'system', id: 'document' }
        : { type: 'custom', id: selectedTemplateId.value };

    const payload: Record<string, unknown> = {
        template,
        related: props.docIds.map((docId) => ({
            type: props.linkedtype,
            id: Number(docId),
        })),
    };
    if (scheduledDate.value) {
        payload.scheduled_at = scheduledDate.value;
    }
    return payload;
};

const handleSend = async (): Promise<void> => {
    isSending.value = true;
    try {
        await sendBatchEmail(buildBatchPayload());
        const toastText = isScheduledSend.value
            ? $t.__('email.massSend.successScheduledToast')
            : $t.__('email.massSend.successToast');
        $pushToast({
            type: CompassEnums.ToastType.SUCCESS,
            icon: true,
            text: toastText,
        });
        emit('close-panel');
    } catch (error: unknown) {
        const status = (error as { status?: number })?.status;
        if (status !== 402) {
            $pushToast({
                type: CompassEnums.ToastType.ERROR,
                icon: true,
                text: $t.__('common.labels.toast.error'),
            });
        }
    } finally {
        isSending.value = false;
        scheduledDate.value = null;
    }
};

watch(
    () => props.isPanelMinimized,
    (newValue: boolean) => {
        nextTick(() => {
            if (compassPanelRef.value) {
                compassPanelRef.value.isMinimized = newValue;
            }
        });
    },
);

onMounted(async (): Promise<void> => {
    try {
        await Promise.all([loadEmailSettings(), loadTemplates(), loadTags(), loadSystemTemplate()]);
    } catch {
        $pushToast({
            type: CompassEnums.ToastType.ERROR,
            icon: true,
            text: $t.__('common.labels.toast.error'),
        });
    } finally {
        isLoading.value = false;
    }
});
</script>

<style lang="scss">
.email-mass-send-panel__template-menu {
    z-index: 1001;
}

#compass-dropdown-container {
    .email-mass-send-panel__schedule-dropdown {
        width: 220px;
        z-index: 1001;
    }
}
</style>

<style lang="scss" scoped>
.email-mass-send-panel {
    &__loader {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: var(--spacing-8);
    }
    :deep(.compass-panel__scrollable) {
        overflow: hidden;
    }
    :deep(.compass-panel__content) {
        display: flex;
        flex-direction: column;
        height: 100%;
    }
    &__content {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }
    &__preview {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        overflow-y: auto;
    }
    &__field {
        display: flex;
        align-items: center;
        gap: var(--spacing-1);
        min-height: 32px;
        flex-shrink: 0;
        margin-bottom: var(--spacing-1);
    }
    &__field-value {
        color: var(--colors-common-text-subtle-default);
    }
    &__body {
        color: var(--colors-common-text-subtle-default);
        white-space: pre-wrap;
        flex: 1;
        margin: var(--spacing-1) 0;
    }
    &__template-select {
        width: 320px;
    }
    &__attachments {
        display: flex;
        flex-direction: column;
    }
    &__bottom {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-3);
        flex-shrink: 0;
        :deep(.compass-alert__text-container) {
            white-space: pre-line;
        }
    }

    &__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--spacing-3);
        padding: var(--spacing-3) var(--spacing-5);
        border-top: 1px solid var(--colors-common-border-lighter);
    }
}
</style>
